import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';

// ── Floating popup card system ──────────────────────────────────────────────
// Shared by the collapsed-sidebar nav-section flyout and the user-account
// menu. Both are fixed-position cards anchored just outside the sidebar,
// positioned in JS (not pure CSS) because the anchor point depends on the
// trigger row's live position, which shifts with sidebar collapse/expand,
// scroll, and viewport resize.

const CARD_WIDTH = 230; // must match .sms-popup-card width in index.css
const EDGE_GAP = 8;
const DRAWER_BREAKPOINT = 768; // matches the sidebar->off-canvas-drawer breakpoint in index.css
const CLOSE_GRACE_MS = 160;

function computeCardPosition({ triggerRect, sidebarRect, mode }) {
  const isDrawer = window.innerWidth < DRAWER_BREAKPOINT;

  if (mode === 'account') {
    // Trigger sits in the lower half of the viewport (a sidebar footer, or
    // a full-height mobile drawer's footer) — open upward from it, anchored
    // to the sidebar/drawer's edge, same as before.
    if (triggerRect.top > window.innerHeight / 2) {
      let left = isDrawer ? sidebarRect.left + 12 : sidebarRect.right + EDGE_GAP;
      left = Math.min(left, window.innerWidth - CARD_WIDTH - EDGE_GAP);
      left = Math.max(EDGE_GAP, left);
      return { left, bottom: window.innerHeight - triggerRect.top + 8 };
    }
    // Trigger sits in the upper half (e.g. a mobile topbar avatar button) —
    // open downward, right-aligned under the trigger itself rather than the
    // sidebar's edge, since there's no sidebar edge nearby to anchor to.
    let left = triggerRect.right - CARD_WIDTH;
    left = Math.min(left, window.innerWidth - CARD_WIDTH - EDGE_GAP);
    left = Math.max(EDGE_GAP, left);
    return { left, top: triggerRect.bottom + 8 };
  }

  let left = isDrawer ? sidebarRect.left + 12 : sidebarRect.right + EDGE_GAP;
  left = Math.min(left, window.innerWidth - CARD_WIDTH - EDGE_GAP);
  left = Math.max(EDGE_GAP, left);
  return { left, top: triggerRect.top };
}

// mode: 'flyout' (nav-section) anchors top-aligned to the trigger row.
// mode: 'account' anchors bottom-aligned (opens upward), flipping on mobile.
export function usePopupCard({ getSidebarRect, mode = 'flyout' }) {
  const [openId, setOpenId] = useState(null);
  const [pos, setPos] = useState(null);
  const triggerElRef = useRef(null);
  const cardRef = useRef(null);
  const closeTimerRef = useRef(null);

  const recompute = useCallback(() => {
    const el = triggerElRef.current;
    const sidebarRect = getSidebarRect();
    if (!el || !sidebarRect) return;
    setPos(computeCardPosition({ triggerRect: el.getBoundingClientRect(), sidebarRect, mode }));
  }, [getSidebarRect, mode]);

  const open = useCallback((id, el) => {
    clearTimeout(closeTimerRef.current);
    triggerElRef.current = el;
    setOpenId(id);
    const sidebarRect = getSidebarRect();
    if (el && sidebarRect) {
      setPos(computeCardPosition({ triggerRect: el.getBoundingClientRect(), sidebarRect, mode }));
    }
  }, [getSidebarRect, mode]);

  const toggle = useCallback((id, el) => {
    if (openId === id) { clearTimeout(closeTimerRef.current); setOpenId(null); return; }
    open(id, el);
  }, [openId, open]);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenId(null), CLOSE_GRACE_MS);
  }, []);

  const cancelClose = useCallback(() => { clearTimeout(closeTimerRef.current); }, []);

  const closeNow = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setOpenId(null);
  }, []);

  // Recompute on resize/breakpoint change so a card left open doesn't keep
  // stale coordinates from before the crossing.
  useEffect(() => {
    if (!openId) return;
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [openId, recompute]);

  useEffect(() => {
    if (!openId) return;
    function onDown(e) {
      if (triggerElRef.current?.contains(e.target)) return;
      if (cardRef.current?.contains(e.target)) return;
      setOpenId(null);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openId]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') setOpenId(null); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  return { openId, pos, cardRef, open, toggle, scheduleClose, cancelClose, closeNow };
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function PopupCard({ pos, cardRef, label, onMouseEnter, onMouseLeave, children }) {
  if (!pos) return null;
  return createPortal(
    <div
      ref={cardRef}
      className="sms-popup-card"
      role="menu"
      style={{
        left: pos.left,
        top: pos.top,
        bottom: pos.bottom,
        animation: prefersReducedMotion() ? 'none' : undefined,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {label && <div className="sms-popup-card-label">{label}</div>}
      <div className="sms-popup-card-items">{children}</div>
    </div>,
    document.body
  );
}

export function PopupCardLink({ to, icon, label, active, badge, badgeTone, badgeLabel, onClick }) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className={`sms-popup-card-item ${active ? 'active' : ''}`}
      aria-label={badgeLabel}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span>{label}</span>
      {badge != null && <span className={`sms-popup-card-badge ${badgeTone ? `sms-popup-card-badge-${badgeTone}` : ''}`} aria-hidden="true">{badge}</span>}
    </NavLink>
  );
}

export function PopupCardButton({ icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`sms-popup-card-item ${danger ? 'danger' : ''}`}
      onClick={onClick}
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
